/**
 * Queue API client — Location queue management (web)
 */
import { apiClient } from '../../../services/apiClient';

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  switch (method) {
    case 'GET': return apiClient.get<T>(path);
    case 'POST': return apiClient.post<T>(path, body);
    default: throw new Error(`Unsupported method: ${method}`);
  }
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TrackInfoDto {
  id: string;
  title: string;
  artist?: string | null;
  album?: string | null;
  genre?: string | null;
  tags?: string[] | null;
  durationSeconds: number;
  category?: string | null;
  thumbnailUrl?: string | null;
}

export interface QueueItemDto {
  id: string;
  position: number;
  track: TrackInfoDto;
  sourcePlaylistId?: string | null;
  enqueueAction?: string | null;
  isPlayed: boolean;
  playedAt?: string | null;
}

export interface LocationQueueStateDto {
  locationId: string;
  scenarioName?: string | null;
  currentPosition: number;
  totalItems: number;
  items: QueueItemDto[];
}

export interface EnqueueRequestDto {
  contentId: string;
  action: 'playNow' | 'playNext' | 'addToQueue';
}

export interface EnqueueResponseDto {
  message: string;
  queueItemId: string;
  position: number;
}

export interface NowPlayingDto {
  currentTrack?: TrackInfoDto | null;
  trackProgress?: TrackProgressDto | null;
  scenarioName?: string | null;
  playlistName?: string | null;
  upcomingTracks: TrackInfoDto[];
}

export interface TrackProgressDto {
  elapsedSeconds: number;
  totalSeconds: number;
}

// ---------------------------------------------------------------------------
// Queue API
// ---------------------------------------------------------------------------

export const getQueue = (locationId: string, limit = 20) =>
  request<LocationQueueStateDto>('GET', `/api/v1/player/locations/${locationId}/queue?limit=${limit}`);

export const enqueue = (locationId: string, body: EnqueueRequestDto) =>
  request<EnqueueResponseDto>('POST', `/api/v1/player/locations/${locationId}/queue/enqueue`, body);

export const skipTrack = (locationId: string) =>
  request<NowPlayingDto>('POST', `/api/v1/player/locations/${locationId}/queue/skip`);

export const previousTrack = (locationId: string) =>
  request<NowPlayingDto>('POST', `/api/v1/player/locations/${locationId}/queue/previous`);

export const getNowPlaying = async (locationId: string): Promise<NowPlayingDto | null> => {
  // apiClient.get returns undefined on 204 — coerce to null for the existing call sites.
  const result = await apiClient.get<NowPlayingDto | undefined>(
    `/api/v1/player/locations/${locationId}/now-playing`,
  );
  return result ?? null;
};
